function addConsoleCommands(parser) {
  parser.registerCommand('apply', (posArgs, kvArgs) => {
    const consoleComponent = document.querySelector('[console]').components.console;
    const selectedEntity = consoleComponent.selectedEntity;

    if (!selectedEntity) {
      return 'Error: No entity selected. Click on an entity in the scene to select it.';
    }

    const [feature, ...args] = posArgs;

    switch (feature) {
      case 'shader':
        if (args.length !== 1) {
          return 'Error: Shader application requires exactly one argument. Usage: /apply shader shaderName';
        }
        selectedEntity.setAttribute('material', 'shader', args[0]);
        return `Applied shader "${args[0]}" to selected entity.`;

      case 'material':
        if (args.length !== 2) {
          return 'Error: Material property application requires exactly two arguments. Usage: /apply material propertyName value';
        }
        const [property, value] = args;
        selectedEntity.setAttribute('material', property, value);
        return `Applied material property "${property}: ${value}" to selected entity.`;

      case 'component':
        if (args.length < 1) {
          return 'Error: Component application requires at least one argument. Usage: /apply component componentName [propertyName value]';
        }
        const [componentName, ...componentArgs] = args;
        const componentData = {};
        for (let i = 0; i < componentArgs.length; i += 2) {
          componentData[componentArgs[i]] = componentArgs[i + 1];
        }
        selectedEntity.setAttribute(componentName, componentData);
        return `Applied component "${componentName}" to selected entity with properties: ${JSON.stringify(componentData)}`;

      default:
        return `Error: Unknown feature "${feature}". Available features are: shader, material, component`;
    }
  }, {
    description: 'Apply features to the selected entity',
    args: [
      { name: 'feature', type: 'string', description: 'Feature to apply (shader, material, or component)' },
      { name: '...args', type: 'string', description: 'Arguments for the feature (varies based on feature type)' }
    ],
    examples: [
      '/apply shader standard',
      '/apply material roughness 0.5',
      '/apply component position x 1 y 2 z 3'
    ]
  });


    parser.registerCommand('setPosition', (posArgs, kvArgs) => {
        if (posArgs.length !== 4) {
          throw new Error('setPosition requires 4 arguments: player, x, y, z');
        }
        const [player, x, y, z] = posArgs;
        return `Setting ${player}'s position to (${x}, ${y}, ${z})`;
      }, {
        description: 'Set the position of a player in 3D space',
        args: [
          { name: 'player', type: 'string', description: 'Player name or ID' },
          { name: 'x', type: 'number', description: 'X coordinate' },
          { name: 'y', type: 'number', description: 'Y coordinate' },
          { name: 'z', type: 'number', description: 'Z coordinate' }
        ],
        examples: ['/setPosition player1 10 20 30']
      });

      parser.registerCommand('addEntity', (posArgs, kvArgs) => {
        const [entityId, type, x, y, z] = posArgs;
        
        // Check if all required arguments are provided
        if (posArgs.length < 5) {
          return 'Error: Insufficient arguments. Usage: /addEntity entityId type x y z';
        }
      
        // Validate entity type
        const validTypes = ['box', 'sphere', 'cylinder', 'plane', 'cone', 'torus'];
        if (!validTypes.includes(type)) {
          return `Error: Invalid entity type. Valid types are: ${validTypes.join(', ')}`;
        }
      
        // Create the entity
        const entity = document.createElement('a-entity');
        entity.id = entityId;
        entity.setAttribute('geometry', { primitive: type });
        entity.setAttribute('position', { x, y, z });
        
        // Add material if provided
        if (kvArgs.color) {
          entity.setAttribute('material', { color: kvArgs.color });
        }
      
        // Add the entity to the scene
        const scene = document.querySelector('a-scene');
        if (scene) {
          scene.appendChild(entity);
          return `Added ${type} entity with ID '${entityId}' at position (${x}, ${y}, ${z})`;
        } else {
          return 'Error: A-Frame scene not found';
        }
      }, {
        description: 'Add a new entity to the scene with a specified ID',
        args: [
          { name: 'entityId', type: 'string', description: 'Unique ID for the new entity' },
          { name: 'type', type: 'string', description: 'Type of entity to add (e.g., box, sphere, cylinder)' },
          { name: 'x', type: 'number', description: 'X position' },
          { name: 'y', type: 'number', description: 'Y position' },
          { name: 'z', type: 'number', description: 'Z position' }
        ],
        examples: [
          '/addEntity myBox box 0 1 -3',
          '/addEntity redSphere sphere 2 1.5 -4 color:red'
        ]
      });

      parser.registerCommand('removeEntity', (posArgs, kvArgs) => {
        const [entityId] = posArgs;
        
        // Check if the entity ID is provided
        if (!entityId) {
          return 'Error: Entity ID is required. Usage: /removeEntity entityId';
        }
      
        // Find the entity in the scene
        const entity = document.querySelector(`#${entityId}`);
        
        if (entity) {
          // Check if the entity is part of the scene
          const scene = document.querySelector('a-scene');
          if (scene && scene.contains(entity)) {
            // Remove the entity from the scene
            entity.parentNode.removeChild(entity);
            return `Removed entity with ID '${entityId}' from the scene`;
          } else {
            return `Error: Entity with ID '${entityId}' is not a child of the scene`;
          }
        } else {
          return `Error: Entity with ID '${entityId}' not found`;
        }
      }, {
        description: 'Remove an entity from the scene by its ID',
        args: [
          { name: 'entityId', type: 'string', description: 'ID of the entity to remove' }
        ],
        examples: [
          '/removeEntity myBox',
          '/removeEntity player'
        ]
      });

      parser.registerCommand('toggleWireframe', (posArgs, kvArgs) => {
        const scene = document.querySelector('a-scene');
        if (!scene) {
          return 'Error: A-Frame scene not found';
        }
      
        // Check if wireframe mode is already set
        const isWireframe = scene.hasAttribute('wireframe-mode');
      
        if (isWireframe) {
          // Disable wireframe mode
          scene.removeAttribute('wireframe-mode');
          scene.querySelectorAll('[material]').forEach(el => {
            const material = el.getAttribute('material');
            if (material.wireframe) {
              delete material.wireframe;
              el.setAttribute('material', material);
            }
          });
          return 'Wireframe mode disabled';
        } else {
          // Enable wireframe mode
          scene.setAttribute('wireframe-mode', '');
          scene.querySelectorAll('[material]').forEach(el => {
            const material = el.getAttribute('material');
            material.wireframe = true;
            el.setAttribute('material', material);
          });
          return 'Wireframe mode enabled';
        }
      }, {
        description: 'Toggle wireframe mode for all entities in the scene',
        args: [],
        examples: ['/toggleWireframe']
      });
      
      // Add this component to your A-Frame scene
      AFRAME.registerComponent('wireframe-mode', {
        init: function () {
          this.el.addEventListener('child-attached', this.updateWireframe.bind(this));
        },
        
        updateWireframe: function (evt) {
          const el = evt.detail.el;
          if (el.getAttribute('material')) {
            const material = el.getAttribute('material');
            material.wireframe = true;
            el.setAttribute('material', material);
          }
        },
      
        remove: function () {
          this.el.querySelectorAll('[material]').forEach(el => {
            const material = el.getAttribute('material');
            if (material.wireframe) {
              delete material.wireframe;
              el.setAttribute('material', material);
            }
          });
        }
      });

      parser.registerCommand('generateAndApply', (posArgs, kvArgs) => {
        const userPrompt = posArgs.join(' ');
        if (!userPrompt) {
          return 'Error: Please provide a prompt for the LLM. Usage: /generateAndApply your prompt here';
        }
      
        // Pre-built prompt template with user prompt integration
        const promptTemplate = `
      Generate HTML, CSS, and JavaScript code for an A-Frame.js game based on the following prompt:
      
      "${userPrompt}"
      
      Please follow these guidelines:
      1. Separate your JavaScript, HTML, and CSS code.
      2. Add a comment at the beginning of each code block:
         - Use <!-- HTML --> for HTML
         - Use // JavaScript for JavaScript
         - Use /* CSS */ for CSS
      3. Ensure the HTML includes appropriate data-aframe attributes for A-Frame integration.
      4. The JavaScript should include at least one custom A-Frame component.
      
      Provide your response in the following format:
      
      <!-- HTML -->
      (Your HTML code here)
      
      /* CSS */
      (Your CSS code here)
      
      // JavaScript
      (Your JavaScript code here)
      `;
      
        // Create a sandbox div to contain the generated content
        const sandboxId = 'llm-generated-sandbox-' + Date.now();
        const sandbox = document.createElement('div');
        sandbox.id = sandboxId;
        document.querySelector('a-scene').appendChild(sandbox);
      
        // Make the AJAX call to the backend
        $.ajax({
          url: '/api/llm-generate', // Replace with your actual endpoint
          method: 'POST',
          data: JSON.stringify({ prompt: promptTemplate }),
          contentType: 'application/json',
          dataType: 'json',
          success: function(response) {
            if (response.content) {
              // Parse the response
              const parsedContent = parseGeneratedContent(response.content);
              if (parsedContent.html && parsedContent.css && parsedContent.js) {
                // Apply the generated content
                applyGeneratedContent(sandboxId, parsedContent.html, parsedContent.css, parsedContent.js);
                return `Generated content applied in sandbox: #${sandboxId}`;
              } else {
                return 'Error: Invalid response format from LLM';
              }
            } else {
              return 'Error: Invalid response from LLM';
            }
          },
          error: function(jqXHR, textStatus, errorThrown) {
            return `Error: ${textStatus} - ${errorThrown}`;
          }
        });
      
        return 'Generating and applying content...';
      }, {
        description: 'Generate and apply HTML, CSS, and JavaScript to the A-Frame scene using an LLM',
        args: [
          { name: 'prompt', type: 'string', description: 'Prompt for the LLM to generate content' }
        ],
        examples: ['/generateAndApply Create a rotating red cube']
      });
      
      function parseGeneratedContent(content) {
        const htmlRegex = new RegExp('<!-- HTML -->([\\s\\S]*?)(?=/\\* CSS \\*/|// JavaScript|$)');
        const cssRegex = new RegExp('/\\* CSS \\*/([\\s\\S]*?)(?=<!-- HTML -->|// JavaScript|$)');
        const jsRegex = new RegExp('// JavaScript([\\s\\S]*?)(?=<!-- HTML -->|/\\* CSS \\*/|$)');
      
        const htmlMatch = content.match(htmlRegex);
        const cssMatch = content.match(cssRegex);
        const jsMatch = content.match(jsRegex);
      
        return {
          html: htmlMatch ? htmlMatch[1].trim() : '',
          css: cssMatch ? cssMatch[1].trim() : '',
          js: jsMatch ? jsMatch[1].trim() : ''
        };
      }
      
      function applyGeneratedContent(sandboxId, html, css, js) {
        const sandbox = document.getElementById(sandboxId);
        
        // Apply HTML
        sandbox.innerHTML = html;
        
        // Apply CSS
        const style = document.createElement('style');
        style.textContent = css;
        sandbox.appendChild(style);
        
        // Apply JavaScript
        const script = document.createElement('script');
        script.textContent = `
          (function() {
            const sandbox = document.getElementById('${sandboxId}');
            ${js}
          })();
        `;
        sandbox.appendChild(script);
        
        // Integrate with A-Frame
        integrateWithAFrame(sandboxId);
      }
      
      function integrateWithAFrame(sandboxId) {
        const sandbox = document.getElementById(sandboxId);
        const scene = document.querySelector('a-scene');
        
        // Convert relevant HTML elements to A-Frame entities
        sandbox.querySelectorAll('*').forEach(el => {
          if (el.dataset.aframe) {
            const entity = document.createElement('a-entity');
            entity.setAttribute('geometry', el.dataset.geometry || '');
            entity.setAttribute('material', el.dataset.material || '');
            entity.setAttribute('position', el.dataset.position || '');
            entity.setAttribute('rotation', el.dataset.rotation || '');
            entity.setAttribute('scale', el.dataset.scale || '');
            entity.setAttribute('llm-generated', '');
            scene.appendChild(entity);
          }
        });
        
        // Register any custom A-Frame components defined in the generated JS
        if (typeof AFRAME !== 'undefined') {
          AFRAME.registerComponent('llm-generated', {
            init: function() {
              // This component can be used to identify and manage LLM-generated entities
            }
          });
        }
      }

}



export default addConsoleCommands;